import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("ibloom_token");
    const sess = localStorage.getItem("ibloom_session");
    if (!token && !sess) { setUser(null); setLoading(false); return; }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      // Only wipe the token when the server explicitly says the user is gone
      // (401/403). On 5xx, network blips or CORS hiccups, keep the token so
      // the next call can retry — otherwise users get silently logged out on
      // every transient hiccup and the Profile tab shows "Sign in" by mistake.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("ibloom_token");
        localStorage.removeItem("ibloom_session");
        setUser(null);
      }
      // For other errors leave `user` as-is so the UI can keep working;
      // a foreground action will surface the real error if any.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (identifier, password) => {
    const res = await api.post("/auth/login", { identifier, password });
    localStorage.setItem("ibloom_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async ({ email, mobile, password, name, role = "consumer" }) => {
    const res = await api.post("/auth/register", { email: email || null, mobile: mobile || null, password, name, role });
    localStorage.setItem("ibloom_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const loginAsGuest = async () => {
    const res = await api.post("/auth/guest");
    localStorage.setItem("ibloom_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const loginWithEmergentSession = async (sessionId) => {
    const res = await api.post("/auth/session", { session_id: sessionId });
    localStorage.setItem("ibloom_token", res.data.token);
    localStorage.setItem("ibloom_session", res.data.session_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("ibloom_token");
    localStorage.removeItem("ibloom_session");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, loginWithEmergentSession, loginAsGuest, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
